import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { Profile } from "@/hooks/designer-grid/types";
import { ContentPagination } from "@/v2/services/types/serviceInterfaces";
import { ALL_COUNTRIES } from '@/data/countries';

// Type definitions for profile data
export interface ProfileData {
  id: string;
  username: string | null;
  username_slug: string | null;
  location: string | null;
  profession: string | null;
  avatar_url: string | null;
  is_admin: boolean;
  bio: string | null;
  years_experience: number | null;
  skills: string[] | null;
  education: string | null;
  languages: string[] | null;
  portfolio_url: string | null;
  instagram_username: string | null;
  linkedin_username: string | null;
  view_count: number;
  like_count: number;
  created_at: string;
  updated_at: string;
  portfolio_items?: any[];
  experience_items?: any[];
  hire_me_enabled: boolean;
  display_name?: string | null;
  is_featured?: boolean;
  trending_score?: number;
}

// Hook for profile filters
export const useProfileFilters = () => {
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedDisciplines, setSelectedDisciplines] = useState<string[]>([]);
  const [isFeatured, setIsFeatured] = useState<boolean | null>(null);

  const handleLocationToggle = (location: string) => {
    setSelectedLocations((prev) =>
      prev.includes(location)
        ? prev.filter((loc) => loc !== location)
        : [...prev, location]
    );
  };

  const handleDisciplineToggle = (discipline: string) => {
    setSelectedDisciplines((prev) =>
      prev.includes(discipline)
        ? prev.filter((disc) => disc !== discipline)
        : [...prev, discipline]
    );
  };

  const handleFeaturedToggle = (featured: boolean | null) => {
    setIsFeatured(featured);
  };

  const handleFilterReset = () => {
    setSelectedLocations([]);
    setSelectedDisciplines([]);
    setIsFeatured(null);
  };

  return {
    selectedLocations,
    selectedDisciplines,
    isFeatured,
    handleLocationToggle,
    handleDisciplineToggle,
    handleFeaturedToggle,
    handleFilterReset,
  };
};

/**
 * Hook to fetch profiles with filtering and pagination
 */
export const useProfiles = () => {
  const queryClient = useQueryClient();
  const {
    selectedLocations,
    selectedDisciplines,
    isFeatured,
    handleLocationToggle,
    handleDisciplineToggle,
    handleFeaturedToggle,
    handleFilterReset,
  } = useProfileFilters();
  const [page, setPage] = useState(1);
  const limit = 12;
  const category = "trending";

  // Map selectedLocations from ids to labels
  const getCountryLabel = (id) => ALL_COUNTRIES.find(c => c.id === id)?.label || id;
  const selectedLocationLabels = selectedLocations.map(getCountryLabel);

  const result = useQuery({
    queryKey: [
      "profiles",
      {
        disciplines: selectedDisciplines,
        locations: selectedLocationLabels,
        category,
        limit,
        page,
        isFeatured,
      },
    ],
    queryFn: async () => {
      // Construct query parameters
      const params: any = {
        disciplines: selectedDisciplines.length ? selectedDisciplines : null,
        locations: selectedLocationLabels.length ? selectedLocationLabels : null,
        category,
        limit_count: limit,
        offset_value: (page - 1) * limit,
      };

      // Call the RPC function to get profiles with offset-based pagination
      const { data, error } = await supabase.rpc(
        "get_profiles_with_offset",
        params
      );

      if (error) throw error;

      // Check if data exists and has the expected structure
      if (!data || !data.profiles || !Array.isArray(data.profiles)) {
        return { profiles: [], totalCount: 0, pagination: null };
      }

      // Log raw data from API
      console.log('[Debug] Raw profile data from API:', data.profiles[0]);

      // Process profiles to ensure they match the Profile type
      const profiles = (data.profiles || []).map((profile: any) => {
        const transformedProfile = {
          id: profile.id,
          username: profile.username,
          username_slug: profile.username_slug,
          profession: profile.profession,
          location: profile.location,
          avatar_url: profile.avatar_url,
          is_admin: profile.is_admin || false,
          is_featured: profile.is_featured || false,
          skills: profile.skills || [],
          view_count: profile.view_count || 0,
          like_count: profile.like_count || 0,
          is_liked: profile.is_liked || false,
          display_name: profile.display_name || profile.username,
          hire_me_enabled: profile.hire_me_enabled || false,
          trending_score: profile.trending_score || 0,
          is_trending: (profile.trending_score || 0) > 10
        };

        // Log transformed profile
        console.log('[Debug] Transformed profile:', {
          id: transformedProfile.id,
          hire_me_enabled: transformedProfile.hire_me_enabled,
          raw_hire_me_enabled: profile.hire_me_enabled
        });

        return transformedProfile;
      });

      const totalCount =
        typeof data.total_count === "number" ? data.total_count : 0;
      const totalPages = Math.ceil(totalCount / limit);
      const currentPage = page;
      const hasNextPage = currentPage < totalPages;
      const hasPrevPage = currentPage > 1;

      // Return both the profiles array and pagination info
      return {
        profiles,
        totalCount,
        pagination: {
          currentPage,
          totalPages,
          hasNextPage,
          hasPrevPage,
          totalCount,
        },
      };
    },
  });

  // Function to handle page changes
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  return {
    ...result,
    handlePageChange,
    filters: {
      selectedLocations,
      selectedDisciplines,
      isFeatured,
      handleLocationToggle,
      handleDisciplineToggle,
      handleFeaturedToggle,
      handleFilterReset,
    },
  };
};

export const useFeaturedProfiles = () => {
  const queryClient = useQueryClient();
  const {
    selectedLocations,
    selectedDisciplines,
    handleLocationToggle,
    handleDisciplineToggle,
    handleFilterReset,
  } = useProfileFilters();
  const [page, setPage] = useState(1);
  const limit = 12;

  const result = useQuery({
    queryKey: [
      "profiles",
      {
        disciplines: selectedDisciplines,
        locations: selectedLocations,
        limit,
        page,
      },
    ],
    queryFn: async () => {
      // Construct query parameters
      const params = {
        professions: selectedDisciplines.length ? selectedDisciplines : null,
        locations: selectedLocations.length ? selectedLocations : null,
        limit_count: limit,
        offset_value: (page - 1) * limit,
      };

      // Call the RPC function to get profiles with offset-based pagination
      const { data, error } = await supabase.rpc(
        "get_featured_profiles",
        params
      );

      if (error) throw error;

      // Check if data exists and has the expected structure
      if (!data || !data.profiles || !Array.isArray(data.profiles)) {
        return { profiles: [], totalCount: 0, pagination: null };
      }
      // Process profiles to ensure they match the Profile type
      const profiles = data.profiles.map((profile: any) => ({
        id: profile.id,
        username: profile.username,
        username_slug: profile.username_slug,
        profession: profile.profession,
        location: profile.location,
        avatar_url: profile.avatar_url,
        is_admin: profile.is_admin || false,
        is_featured: profile.is_featured || false,
        skills: profile.skills || [],
        view_count: profile.view_count || 0,
        like_count: profile.like_count || 0,
        is_liked: profile.is_liked || false,
        display_name: profile.display_name || profile.username,
        hire_me_enabled: profile.hire_me_enabled || false,
        trending_score: profile.trending_score || 0,
        is_trending: (profile.trending_score || 0) > 10,
        portfolio_items: profile.portfolio_items || [],
        updated_at: profile.updated_at
      }));

      const totalCount =
        typeof data.total_count === "number" ? data.total_count : 0;
      const totalPages = Math.ceil(totalCount / limit);
      const currentPage = page;
      const hasNextPage = currentPage < totalPages;
      const hasPrevPage = currentPage > 1;

      // Return both the profiles array and pagination info
      return {
        profiles,
        totalCount,
        pagination: {
          currentPage,
          totalPages,
          hasNextPage,
          hasPrevPage,
          totalCount,
        },
      };
    },
  });

  // Function to handle page changes
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  return {
    ...result,
    handlePageChange,
    filters: {
      selectedLocations,
      selectedDisciplines,
      handleLocationToggle,
      handleDisciplineToggle,
      handleFilterReset,
    },
  };
};
/**
 * Hook to fetch a single profile by ID or username
 */
export const useProfile = (idOrSlug: string | undefined, options = {}) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ["profile", idOrSlug],
    queryFn: async () => {
      if (!idOrSlug) {
        throw new Error("Profile ID or slug is required");
      }

      // Determine if the identifier is a UUID or a username slug
      const isUuid = idOrSlug.match(
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
      );

      let profileQuery;

      if (isUuid) {
        // Query by ID
        profileQuery = supabase
          .from("profiles")
          .select("*")
          .eq("id", idOrSlug)
          .single();
      } else {
        // Query by username slug
        profileQuery = supabase
          .from("profiles")
          .select("*")
          .eq("username_slug", idOrSlug)
          .single();
      }

      const { data, error } = await profileQuery;

      if (error) throw error;

      return { data };
    },
    ...options,
  });
};
